import { useState } from "react"
import { motion } from "framer-motion"
import { flattenData, useQuery, gql, API } from "utils/api"
import { IMAGE, SEO } from "utils/fragments"
import * as Yup from "yup"

import { Button, Header, Page, Section, Tabs, Title, WysiwygText } from "components/shared"
import { Field, Form } from "components/form"
import { Field as FormikField } from "formik"
import axios from "axios"
import { useAnimation } from "utils/useAnimation"
import * as Sentry from "@sentry/nextjs"
import { PhoneNumberField } from "components/consultation/OnlineConsultationForm"

const ContactForm = ({ successMessage }) => {
  const [formComplete, setFormComplete] = useState(false)
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Required"),
    email: Yup.string().required("Required"),
    phone: Yup.string().required("Required"),
  })

  return (
    <Form
      initialValues={{
        name: "",
        email: "",
        phone: "",
        message: "",
        website: "",
      }}
      validationSchema={validationSchema}
      formComplete={formComplete}
      successMessage={successMessage}
      onSubmit={(values, { setSubmitting, setStatus, resetForm }) => {
        setStatus()

        if (values.website.trim()) {
          resetForm()
          setFormComplete(true)
          return
        }

        setSubmitting(true)
        const fields = { ...values }
        delete fields.website
        const valuesToSubmit = {
          data: {
            ...fields,
            phone: `+${fields.phone}`,
          },
        }
        axios
          .post(`${API}/api/form-contacts`, valuesToSubmit)
          .then(() => {
            setSubmitting(false)
            resetForm()
            setFormComplete(true)
            // setTimeout(() => {
            //   setFormComplete(false)
            // }, 10000)
          })
          .catch((err) => {
            setSubmitting(false)
            setStatus("Error with contact request. Please contact us via phone, email or whatsapp if the error persists.")

            // Capture the error in Sentry
            Sentry.captureException(err, {
              extra: {
                formValues: values,
                component: "ContactForm",
                action: "form submission",
              },
            })
          })
      }}
    >
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label htmlFor="website">Website</label>
        <FormikField id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      <Field name="name" label="Name" placeholder="Enter your name" />
      <Field name="email" label="Email" placeholder="Enter your email" type="email" />

      <PhoneNumberField />
      <Field name="message" label="Message" placeholder="Enter you message" as="textarea" />
    </Form>
  )
}

const Location = ({ address, openingHours, phone, mapEmbedLink, mapLink }) => {
  const { variants, animation } = useAnimation()
  const { animation: containerAnimation } = useAnimation("container")

  return (
    <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">
      <motion.div className="flex-3" {...animation}>
        <iframe title="Map for getting to Tir Na Nog Clinic" src={mapEmbedLink} width="100%" height="450" loading="lazy"></iframe>
      </motion.div>
      <div className="flex-2 flex items-center">
        <motion.div className="flex flex-col justify-center items-start lg:mx-auto lg:max-w-[340px]" {...containerAnimation}>
          {/* <div className="font-semibold uppercase">{title}</div> */}
          <motion.div className="font-semibold uppercase" variants={variants}>
            Address
          </motion.div>
          <div className="mt-4 mb-8 space-y-4">
            <motion.p className="font-medium" variants={variants}>
              <a href={mapLink} target="_blank" rel="noopener noreferrer">
                {address}
              </a>
            </motion.p>
            <motion.div variants={variants}>{openingHours}</motion.div>
          </div>
          <Button size="sm" animate animateOnShow={false}>
            Call us: <span className="ml-2">{phone}</span>
          </Button>
        </motion.div>
      </div>
    </div>
  )
}

const Contact = ({ contactPage: { seo, image, description, successMessage, locations } }) => {
  const { variants } = useAnimation()
  const { animation } = useAnimation("container")

  const locationTabs = locations.map((location) => {
    return {
      label: location.title,
      component: () => <Location {...location} />,
    }
  })

  return (
    <Page seo={seo}>
      <Header layout="contact" title="Get in touch" pretitle="general enquiry" image={image}>
        <motion.div className="py-12 space-y-8 w-full" {...animation}>
          <div className="space-y-4">
            <motion.h2 className="font-semibold text-xl" variants={variants}>
              Personal Information
            </motion.h2>
            <WysiwygText text={description} animate />
          </div>
          <ContactForm successMessage={successMessage} />
        </motion.div>
      </Header>
      <Section className="pt-12 pb-24 lg:!py-32" contentClassName="space-y-12" id="locations" animate>
        <Title title="Where to find us" pretitle="Locations" animate />
        <Tabs layoutId="contact" className="space-y-12" tabs={locationTabs} />
      </Section>
    </Page>
  )
}

export async function getStaticProps() {
  const query = gql`
    ${IMAGE}
    ${SEO}
    query ContactPage {
      contactPage {
        data {
          attributes {
            seo {
              ...seo
            }
            description
            successMessage
            image {
              ...Image
            }
            locations {
              title
              address
              phone
              openingHours
              mapEmbedLink
              mapLink
            }
          }
        }
      }
    }
  `
  const { data } = await useQuery(query)
  return {
    props: { ...flattenData(data) },
    revalidate: 60,
  }
}

export default Contact
